import * as utils from '@/common/utils'
import * as dateUtils from '@/common/dateUtils'

import ApiService from '@/common/api.service'

const module = 'assets/vehicleExpensesModule'

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    expenses: [],
    mode: 'expenses', // Can be 'expenses', 'expense_types', 'client_selection', 'load_expenses'
    client: {
      id: null,
      name: null
    },
    prevClientId: null,
    expensesLoaded: false,
    expenseTypesLoaded: false,
    autoSaveState: null, // Can be null, 'busy', 'success', 'failure'

    // Can be:
    // 'recently_created', 'today' 'yesterday', 'this_week', 'last_week',
    // 'last_7_days', 'this_month', 'last_month', last_30_days', 'custom'
    queryPeriod: 'recently_created',
    queryFrom: dateUtils.dateShort(dateUtils.now()),
    queryTo: dateUtils.dateShort(dateUtils.now()),
    showAllNotes: false,
    search: '',
    sortBy: '',
    sortOrder: '',
    prevSearch: null,
    prevSortBy: null,
    prevSortOrder: null,
    prevSearchTimestamp: null
  }
}

function formDefaults () {
  return {
    vehicles: [],
    expenseTypes: []
  }
}

// Note, transform the vehicles so that each
// vehicle object only has an id and reg_no property.
// The reg_no will include the fleet no. only if it
// is different from the reg. no and not empty.
function transformVehiclesList (vehicles) {
  let results = []
  vehicles.forEach((vehicle) => {
    if (vehicle.fleet_no !== null && vehicle.reg_no !== vehicle.fleet_no) {
      results.push({ id: vehicle.id, reg_no: vehicle.reg_no + ' [' + vehicle.fleet_no + ']' })
    } else {
      results.push({ id: vehicle.id, reg_no: vehicle.reg_no })
    }
  })

  return results
}

function newExpenseObject (clientId, copyFromExpenseObj = null) {
  if (copyFromExpenseObj === null) {
    return {
      id: null,
      client_id: clientId,
      vehicle_id: null,
      vehicle_reg_no: null,
      expense_type_id: null,
      invoice_ref_no: null,
      note: null,
      invoice_date: dateUtils.dateShort(dateUtils.now()),
      unit_count: 1,
      invoice_total: 0.0,
      tax_deductible: false,
      expense_category: 'general', // 'general', 'fuel', 'installment'
      state: 'new', // 'new', 'new-unsaved', 'unmodified', 'modified', 'added', 'updated'
      showDeleteDialog: false,
      addingNote: false,
      showNote: false,
      addingExpenseType: false,
      newExpenseType: null,
      isFocused: false,
      autoSave: false
    }
  } else {
    return {
      id: null,
      client_id: clientId,
      vehicle_id: copyFromExpenseObj.vehicle_id,
      vehicle_reg_no: copyFromExpenseObj.vehicle_reg_no,
      expense_type_id: null,
      invoice_ref_no: null,
      note: null,
      invoice_date: copyFromExpenseObj.invoice_date,
      unit_count: 1,
      invoice_total: 0.0,
      tax_deductible: false,
      expense_category: copyFromExpenseObj.expense_category, // 'general', 'fuel', 'installment'
      state: 'new', // 'new', 'new-unsaved', 'unmodified', 'modified', 'added', 'updated'
      showDeleteDialog: false,
      addingNote: false,
      showNote: false,
      addingExpenseType: false,
      newExpenseType: null,
      isFocused: false,
      autoSave: false
    }
  }
}

function transformFromApi (clientId, expense) {
  return {
    id: expense.id,
    client_id: clientId,
    vehicle_id: expense.vehicle_id,
    vehicle_reg_no: expense.vehicle_reg_no,
    expense_type_id: expense.expense_type_id,
    invoice_ref_no: expense.invoice_ref_no,
    note: expense.note,
    invoice_date: expense.invoice_date,
    unit_count: expense.unit_count,
    invoice_total: expense.invoice_total,
    tax_deductible: expense.tax_deductible,
    expense_category: expense.expense_category,
    state: 'unmodified', // 'new', 'new-unsaved', 'unmodified', 'modified', 'added', 'updated'
    showDeleteDialog: false,
    addingNote: false,
    showNote: false,
    addingExpenseType: false,
    newExpenseType: null,
    isFocused: false,
    autoSave: false
  }
}

/**
 * Calculate the appropriate from and to dates for the given period
 *
 * @param period
 */
function periodFromTo (period) {
  switch (period) {
    case 'today':
      return {
        from: dateUtils.dateShort(dateUtils.now()),
        to: dateUtils.dateShort(dateUtils.now())
      }

    case 'yesterday':
      return {
        from: dateUtils.dateShort(dateUtils.now().subtract(1, 'days')),
        to: dateUtils.dateShort(dateUtils.now().subtract(1, 'days'))
      }

    case 'this_week':
      return {
        from: dateUtils.dateShort(dateUtils.now().startOf('isoweek')),
        to: dateUtils.dateShort(dateUtils.now())
      }

    case 'last_week':
      return {
        from: dateUtils.dateShort(dateUtils.now().subtract(7, 'days').startOf('isoweek')),
        to: dateUtils.dateShort(dateUtils.now().subtract(7, 'days').endOf('isoweek'))
      }

    case 'last_7_days':
      return {
        from: dateUtils.dateShort(dateUtils.now().subtract(7, 'days')),
        to: dateUtils.dateShort(dateUtils.now())
      }

    case 'this_month':
      return {
        from: dateUtils.dateShort(dateUtils.now().startOf('month')),
        to: dateUtils.dateShort(dateUtils.now())
      }

    case 'last_month':
      return {
        from: dateUtils.dateShort(dateUtils.now().subtract(1, 'month').startOf('month')),
        to: dateUtils.dateShort(dateUtils.now().subtract(1, 'month').endOf('month'))
      }

    case 'last_30_days':
      return {
        from: dateUtils.dateShort(dateUtils.now().subtract(30, 'days')),
        to: dateUtils.dateShort(dateUtils.now())
      }

    default:
      return {
        from: dateUtils.dateShort(dateUtils.now()),
        to: dateUtils.dateShort(dateUtils.now())
      }
  }
}

/**
 * Returns appropriate parameters for the given period
 *
 * @param period
 */
function periodParams (period) {
  const fromTo = periodFromTo(period)

  switch (period) {
    case 'today':
      return { invoice_date: fromTo.from }

    case 'yesterday':
      return { invoice_date: fromTo.from }

    case 'this_week':
      return { invoice_date: fromTo.from + '~' }

    case 'last_week':
      return { invoice_date: fromTo.from + '~' + fromTo.to }

    case 'last_7_days':
      return { invoice_date: fromTo.from + '~' }

    case 'this_month':
      return { invoice_date: fromTo.from + '~' }

    case 'last_month':
      return { invoice_date: fromTo.from + '~' + fromTo.to }

    case 'last_30_days':
      return { invoice_date: fromTo.from + '~' }

    default:
      return { invoice_date: dateUtils.dateShort(dateUtils.now()) }
  }
}

function customPeriodParams (fromDate, toDate) {
  return { invoice_date: fromDate + '~' + toDate }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }
}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`vehicles/expenses/${id}`)
      .then((response) => {
        context.commit('updateExpense', response.data.data)
        return response
      }).catch((response) => {
        utils.storeSetIsLoading(module, false)
        return response
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    const clientId = context.getters['get'].client.id
    return ApiService
      .get(`vehicles/expenses/create/client/${clientId}`)
      .then((response) => {
        context.commit('setFormData', response.data.data)
        context.commit('expenseTypesLoaded', true)
        context.commit('setIsModified', false)
        return response
      }).catch((response) => {
        utils.storeSetIsLoading(module, false)
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    context.commit('clearExpenses')
    const clientId = context.getters['get'].client.id
    const queryPeriod = context.getters['get'].queryPeriod
    let additionalParams
    if (queryPeriod === 'custom') {
      const queryFrom = context.getters['get'].queryFrom
      const queryTo = context.getters['get'].queryTo
      additionalParams = { ...params, ...customPeriodParams(queryFrom, queryTo) }
    } else {
      additionalParams = { ...params, ...periodParams(queryPeriod) }
    }
    return ApiService
      .get(`vehicles/expenses/by-client/${clientId}`, additionalParams)
      .then((response) => {
        context.commit('populateExpenses', response.data.data)
        context.commit('expensesLoaded', true)
        return response
      }).catch((response) => {
        utils.storeSetIsLoading(module, false)
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params
   * @return Promise
   */
  queryRecent (context, params) {
    context.commit('clearExpenses')
    const clientId = context.getters['get'].client.id
    return ApiService
      .get(`vehicles/expenses/by-client/${clientId}/recent`, params)
      .then((response) => {
        context.commit('populateRecentExpenses', response.data.data)
        context.commit('expensesLoaded', true)
        return response
      }).catch((response) => {
        utils.storeSetIsLoading(module, false)
        return response
      })
  },

  /**
   * Saves a new expense.
   *
   * @param context
   * @param data
   */
  post (context, { data }) {
    const expense = context.getters['get'].expenses[data.index]
    return ApiService
      .post(`vehicles/expenses`, {
        client_id: context.getters['get'].client.id,
        vehicle_id: expense.vehicle_id,
        expense_type_id: expense.expense_type_id,
        invoice_ref_no: expense.invoice_ref_no,
        note: expense.note,
        invoice_date: expense.invoice_date,
        unit_count: expense.unit_count,
        invoice_total: parseFloat(expense.invoice_total),
        tax_deductible: expense.tax_deductible,
        expense_category: expense.expense_category
      })
      .then((response) => {
        context.commit('setExpenseValue', { index: data.index, property: 'id', value: response.data.data.id })
        context.commit('setExpenseValue', { index: data.index, property: 'state', value: 'added' })
        return response
      }).catch((response) => {
        utils.storeSetIsLoading(module, false)
        return response
      })
  },

  /**
   * Update an existing expense.
   *
   * @param context
   * @param data
   */
  put (context, { data }) {
    const expense = context.getters['get'].expenses[data.index]
    return ApiService
      .put(`vehicles/expenses/${expense.id}`, {
        client_id: context.getters['get'].client.id,
        vehicle_id: expense.vehicle_id,
        expense_type_id: expense.expense_type_id,
        invoice_ref_no: expense.invoice_ref_no,
        note: expense.note,
        invoice_date: expense.invoice_date,
        unit_count: expense.unit_count,
        invoice_total: parseFloat(expense.invoice_total),
        tax_deductible: expense.tax_deductible,
        expense_category: expense.expense_category
      })
      .then((response) => {
        context.commit('setExpenseValue', { index: data.index, property: 'state', value: 'updated' })
        return response
      }).catch((response) => {
        utils.storeSetIsLoading(module, false)
        return response
      })
  },

  delete (context, { data }) {
    const expense = context.getters['get'].expenses[data.index]
    return ApiService
      .delete(`vehicles/expenses/${expense.id}`)
      .then((response) => {
        context.commit('removeExpense', data.index)
        return response
      }).catch((response) => {
        utils.storeSetIsLoading(module, false)
        return response
      })
  },

  postExpenseType (context, { data }) {
    return ApiService
      .post(`vehicles/expense/types`, {
        client_id: context.getters['get'].client.id,
        name: data.name
      })
      .then((response) => {
        // Inject the new expense type into the expense types
        // array in the form data.
        context.commit('addNewExpenseType', response.data.data)
        return response
      }).catch((response) => {
        utils.storeSetIsLoading(module, false)
        return response
      })
  },

  putExpenseType (context, { data }) {
    return ApiService
      .put(`vehicles/expense/types/${data.id}`, {
        name: data.name
      })
      .then((response) => {
        context.commit('updateExpenseType', response.data.data)
        return response
      }).catch((response) => {
        utils.storeSetIsLoading(module, false)
        return response
      })
  },

  deleteExpenseType (context, id) {
    return ApiService
      .delete(`vehicles/expense/types/${id}`)
      .then((response) => {
        context.commit('removeExpenseType', id)
        return response
      }).catch((response) => {
        utils.storeSetIsLoading(module, false)
        return response
      })
  },

  queryExpenseTypes (context, params) {
    const clientId = context.getters['get'].client.id
    return ApiService
      .get(`vehicles/expense/types/by-client/${clientId}`, params)
      .then((response) => {
        context.commit('populateExpenseTypes', response.data.data)
        return response
      }).catch((response) => {
        utils.storeSetIsLoading(module, false)
        return response
      })
  },

  queryExpenseTypesListing (context) {
    const clientId = context.getters['get'].client.id
    return ApiService
      .get(`vehicles/expense/types/by-client/${clientId}/listing`)
      .then((response) => {
        context.commit('populateExpenseTypes', response.data.data)
        context.commit('expenseTypesLoaded', true)
        return response
      }).catch((response) => {
        utils.storeSetIsLoading(module, false)
        return response
      })
  }
}

export const mutations = {
  /**
   * Adds a new blank expense (almost no fields completed)
   */
  addBlankExpense (state) {
    state.data.expenses.push(newExpenseObject(state.data.client.id))
  },

  /**
   * Adds a new expense and copies the appropriate fields
   * from another expense.
   */
  addAndCopyExpense (state, copyFromIndex) {
    state.data.expenses.push(newExpenseObject(state.data.client.id, state.data.expenses[copyFromIndex]))
  },

  populateRecentExpenses (state, expenses) {
    const clientId = state.data.client.id
    expenses.forEach(expense => {
      state.data.expenses.unshift(transformFromApi(clientId, expense))
    })
  },

  populateExpenses (state, expenses) {
    const clientId = state.data.client.id
    expenses.forEach(expense => {
      state.data.expenses.push(transformFromApi(clientId, expense))
    })
  },

  /**
   * Update an existing expense
   *
   * @param state
   * @param updatedExpense
   */
  updateExpense (state, updatedExpense) {
    let index = state.data.expenses.findIndex(expense => expense.id === updatedExpense.id)
    if (index > -1) {
      state.data.expenses[index].vehicle_id = updatedExpense.vehicle_id
      state.data.expenses[index].expense_type_id = updatedExpense.expense_type_id
      state.data.expenses[index].invoice_ref_no = updatedExpense.invoice_ref_no
      state.data.expenses[index].note = updatedExpense.note
      state.data.expenses[index].invoice_date = updatedExpense.invoice_date
      state.data.expenses[index].unit_count = updatedExpense.unit_count
      state.data.expenses[index].invoice_total = updatedExpense.invoice_total
      state.data.expenses[index].tax_deductible = updatedExpense.tax_deductible
      state.data.expenses[index].expense_category = updatedExpense.expense_category
      state.data.expenses[index].state = 'unmodified'
    }
  },

  removeExpense (state, index) {
    state.data.expenses.splice(index, 1)
  },

  clearExpenses (state) {
    state.data.expenses = []
    state.data.expensesLoaded = false
    state.data.expenseTypesLoaded = false
  },

  /**
   * Set the value of a property on a specific expense.
   *
   * @param state
   * @param index
   * @param property
   * @param value
   */
  setExpenseValue (state, { index, property, value }) {
    state.data.expenses[index][property] = value
  },

  /**
   * Sets the expense which currently have focus and
   * clear the focus from any other expenses that currently
   * have focus.
   *
   * @param state
   * @param index
   */
  setFocusedExpense (state, index) {
    for (let i = 0; i < state.data.expenses.length; i++) {
      if (state.data.expenses[i].isFocused && i !== index) {
        state.data.expenses[i].isFocused = false
        state.data.expenses[i].addingNote = false
      } else if (!state.data.expenses[i].isFocused && i === index) {
        state.data.expenses[i].isFocused = true
      }
    }
  },

  /**
   * Remove the focus from the specified expense (by index).
   *
   * @param state
   * @param index
   */
  unsetFocusedExpense (state, index) {
    state.data.expenses[index].isFocused = false
  },

  hideAllNotes (state) {
    for (let i = 0; i < state.data.expenses.length; i++) {
      if (state.data.expenses[i].showNote) {
        state.data.expenses[i].showNote = false
      }
    }
  },

  setQueryPeriod (state, newPeriod) {
    if (state.data.queryPeriod !== 'custom' && newPeriod === 'custom') {
      const fromTo = periodFromTo(state.data.queryPeriod)
      state.data.queryFrom = fromTo.from
      state.data.queryTo = fromTo.to
    }

    state.data.queryPeriod = newPeriod
  },

  /**
   * Add a new expense type by inserting it in
   * the correct alphabetical position within the
   * existing expense types.
   *
   * @param state
   * @param newExpenseType
   */
  addNewExpenseType (state, newExpenseType) {
    let index = state.form.expenseTypes.findIndex(expenseType => expenseType.name > newExpenseType.name)
    if (index === -1) index = state.form.expenseTypes.length
    state.form.expenseTypes.splice(index, 0, newExpenseType)
  },

  /**
   * Update an existing expense type
   *
   * @param state
   * @param updatedExpenseType
   */
  updateExpenseType (state, updatedExpenseType) {
    let index = state.form.expenseTypes.findIndex(expenseType => expenseType.id === updatedExpenseType.id)
    if (index > -1) {
      state.form.expenseTypes[index].name = updatedExpenseType.name
    }
  },

  populateExpenseTypes (state, expenseTypes) {
    state.form.expenseTypes = []
    expenseTypes.forEach(expenseType => {
      state.form.expenseTypes.push(expenseType)
    })
  },

  removeExpenseType (state, id) {
    let index = state.form.expenseTypes.findIndex(expenseType => expenseType.id === id)
    if (index > -1) {
      state.form.expenseTypes.splice(index, 1)
    }
  },

  clearExpenseTypes (state) {
    state.form.expenseTypes = []
    state.data.expenseTypesLoaded = false
  },

  expensesLoaded (state, value) {
    state.data.expensesLoaded = value
  },

  expenseTypesLoaded (state, value) {
    state.data.expenseTypesLoaded = value
  },

  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign(defaults(), data)
  },

  setFormData (state, data) {
    // Note, we transform the vehicles so that each
    // vehicle object only has an id and reg_no property.
    // The reg_no will include the fleet no. only if it
    // is different from the reg. no and not empty.
    state.form = {
      vehicles: transformVehiclesList(data.vehicles),
      expenseTypes: data.expense_types
    }
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = Object.assign(defaults(), data)
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    utils.pullProp(state.data, name, index)
  },

  /**
   * removes an element from an array of a specific property
   * and replaced it with a new element.
   *
   * @param state   Object
   * @param name    String Name of property
   * @param index   Number The index of the element to remove
   * @param replacement New element to replace the old
   */
  spliceProp (state, { name, index, replacement }) {
    state.isModified = true
    state.data[name].splice(index, 1, replacement)
  },

  /**
   * Set the client id.
   *
   * @param state
   * @param client
   */
  setClient (state, client) {
    state.data.prevClientId = state.data.client.id
    state.data.client.id = client.id
    state.data.client.name = client.name
  },

  /**
   * Clear the client data.
   *
   * @param state
   */
  clearClient (state) {
    state.data.client.id = null
    state.data.client.name = null
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/assets/vehicle-expenses/vehicleExpensesModule.js